import React, { useCallback, useEffect, useState } from 'react';
import { getAliceRecommendedAttributes } from '../../../API/AliceGPT/AliceGPT';
import { Checkbox } from '@progress/kendo-react-inputs';
import InnerLoader from '../../Shared/Loader/InnerLoader';
import { getDatalakeImageUrl } from '../../Helpers/DateLakeStorage';
import { ATTRIBUTE_PLURAL_LABEL } from '../../Helpers/Labels/AttributeLabel';
import { typeText } from '../../Attributes/Fields/AttributeTypeField';
import { convertTypeToDesignerType } from '../../Helpers/Recommendation/AliceGPT';
import EditPicklistDialog from '../../Dashboard/Project/AliceGPT/EditPicklistDialog';
import FailedToLoadError from '../../Shared/Recommendation/Error/FailedToLoadError';
import AttributeTypeDropdown, { recommendationAttributeType } from '../../Shared/Recommendation/AttributeTypeDropdown';
import { useTranslation } from 'react-i18next';
import { OPENAI_MODEL } from '../../../Constants/OpenAI';

function AliceRecommendedAttributes({
    entity, projectDomain, projectDescription, projectLanguage,
    onAttributesLoaded, onSelect, onToggleAllAttributes, onPicklistUpdate, onAttributeTypeChange
}) {

    const { t } = useTranslation();
    const { t: getAttributeTypeTranslation } = useTranslation(['common/attribute_type_dropdown'], { useSuspense: false });

    const [loading, setLoading] = useState({});
    const [fetchingMore, setFetchingMore] = useState({});
    const [picklistAttributeToEdit, setPicklistAttributeToEdit] = useState(null);
    const [showShowMoreLink, setShowShowMoreLink] = useState(true);
    const [editAttributeTypeIndex, setEditAttributeTypeIndex] = useState(-1);

    useEffect(() => {
        setShowShowMoreLink(true);
        setEditAttributeTypeIndex(-1)
    }, [entity.name])

    async function getAttributes() {
        const entityName = entity.name;
        try {
            setLoading(prevValue => ({ ...prevValue, [entityName]: true }));
            const response = await getAliceRecommendedAttributes(entityName, OPENAI_MODEL, [], projectDomain, projectDescription, projectLanguage);
            const attributes = response.map(attributesToStateArrayElement);
            onAttributesLoaded(entityName, attributes);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(prevValue => ({ ...prevValue, [entityName]: false }));
        }
    }

    function attributesToStateArrayElement(attribute) {
        return {
            name: attribute[0],
            type: convertTypeToDesignerType(attribute[1]),
            picklist: attribute[2],
            required: attribute[3],
            unique: attribute[4],
            checked: false,
            selected: false
        }
    }

    function toggleAttributeCheckbox(attributeIndex) {
        onSelect(entity.name, attributeIndex);
    }

    async function handleShowMore() {
        const entityName = entity.name;
        try {
            setFetchingMore(prevValue => ({ ...prevValue, [entityName]: true }));
            let attributeNamesToSkip = entity.attributes.map(attribute => attribute.name);
            attributeNamesToSkip = attributeNamesToSkip.join(",");
            const response = await getAliceRecommendedAttributes(
                entityName, OPENAI_MODEL, attributeNamesToSkip, projectDomain, projectDescription,
                projectLanguage
            );
            const attributes = response.map(attributesToStateArrayElement).filter(attr => {
                return !entity.attributes.some(attribute => attribute.name === attr.name);
            });
            const allAttributes = entity.attributes.concat(attributes);
            onAttributesLoaded(entityName, allAttributes);
            if (allAttributes.length === entity.attributes.length) {
                setShowShowMoreLink(false);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setFetchingMore(prevValue => ({ ...prevValue, [entityName]: false }));
        }
    }

    function handleEditPicklistClick(attribute) {
        setPicklistAttributeToEdit(attribute);
    }

    function handlePicklistUpdate(value) {
        onPicklistUpdate(entity.name, picklistAttributeToEdit.name, value);
        handleEditPicklistClick(null);
    }

    function handleAttributeTypeClick(event, index) {
        setEditAttributeTypeIndex(index);
    }

    function handleAttributeTypeChange(event, index) {
        const attribute = entity.attributes[index];
        onAttributeTypeChange(entity.name, attribute.name, event.target.value.value)
        clearEditAttributeIndex()
    }

    function clearEditAttributeIndex() {
        setEditAttributeTypeIndex(-1);
    }

    function getAttributeType(attributeType) {
        const type = recommendationAttributeType.find((item) => item.value === attributeType);
        return type ? t(type.value, { ns: "common/attribute_type_dropdown" }) : "";
    }

    if (entity.hasAttributesLoadingError) {
        return (
            <FailedToLoadError />
        )
    }

    if (entity.attributesLoading) {
        return (
            <div className='h-100 w-100 d-flex flex-column justify-content-center align-items-center'>
                <img src={getDatalakeImageUrl("loaders/RecommendationLoader.gif")} alt=""/>
                <h3 className="mt-4">{t("entity.recommendation_dialog.loading_message")}</h3>
            </div>
        )
    }

    return (
        <div>
            {
                picklistAttributeToEdit &&
                <EditPicklistDialog
                    title={picklistAttributeToEdit.type === "State" ? t("edit_picklist_dialog.state_title") : t("edit_picklist_dialog.picklist_title")}
                    addOptionText={picklistAttributeToEdit.type === "State" ? t("picklist_string_field.add_state_label") : t("picklist_string_field.add_dropdown_label")}
                    attribute={picklistAttributeToEdit}
                    onCancel={() => handleEditPicklistClick(null)}
                    onClose={() => handleEditPicklistClick(null)}
                    onSubmit={handlePicklistUpdate} />
            }
            <ul className='list-group'>
                <li className='list-group-item'>
                    <h5 className="d-flex align-items-center">
                        <Checkbox
                            checked={entity && entity.attributes && entity.attributes.every(attribute => attribute.checked)}
                            value={entity && entity.attributes && entity.attributes.every(attribute => attribute.checked)}
                            onChange={() => onToggleAllAttributes(entity.name)}
                        />
                        <span className="mb-0 ms-2 ps-1 fw-700">
                            {t("entity.recommendation_dialog.select_all_properties_label")}
                        </span>
                    </h5>
                </li>
                {
                    entity &&
                    entity.attributes &&
                    entity.attributes.map((attribute, index) => (
                        <li className='list-group-item' key={"recommended-attribute-" + index}>
                            <Checkbox
                                checked={attribute.checked}
                                value={attribute.checked}
                                onChange={() => toggleAttributeCheckbox(index)}
                                label={attribute.name}
                            />
                            &nbsp;
                            -
                            &nbsp;
                            {
                                editAttributeTypeIndex === index ?
                                <div className="d-inline-block w-50">
                                    <div className='d-inline-block w-90'>
                                        <AttributeTypeDropdown
                                            value={attribute.type}
                                            onChange={(event) => handleAttributeTypeChange(event, index)} />
                                    </div>
                                    &nbsp;
                                    <button className="btn btn-link m-0 p-0 text-danger" onClick={clearEditAttributeIndex}>
                                        <i className="fa-solid fa-close" />
                                    </button>
                                </div>
                                :
                                <button className="btn btn-link m-0 p-0" onClick={(e) => handleAttributeTypeClick(e, index)}>
                                    {getAttributeType(attribute.type)}
                                </button>
                            }
                            &nbsp;
                            { (attribute.type === "Picklist String" || attribute.type === "State Field") && attribute.picklist && ` - ${attribute.picklist.join(", ")}`}
                            {
                                (attribute.type === "Picklist String" || attribute.type === "State Field")
                                &&
                                <button className='btn btn-link' onClick={() => handleEditPicklistClick(attribute)}>
                                    { attribute.type === "Picklist String" ? t("entity.recommendation_dialog.buttons.edit_picklist_label") : t("entity.recommendation_dialog.buttons.edit_state_label")}
                                </button>
                            }
                            {
                                (attribute.type === "Picklist String" || attribute.type === "State Field") &&
                                (!attribute.picklist || attribute.picklist.length === 0) &&
                                <span className="text-danger">
                                    {
                                        attribute.type === "Picklist String" ?
                                        t("entity.recommendation_dialog.errors.no_picklist_label")
                                        :
                                        t("entity.recommendation_dialog.errors.no_state_label")
                                    }
                                </span>
                            }
                        </li>
                    ))
                }
                {
                    !loading[entity.name] &&
                    showShowMoreLink &&
                    <li className='list-group-item font-size-2'>
                        <button className='btn btn-link' onClick={handleShowMore} disabled={fetchingMore[entity.name]}>
                            {t("entity.recommendation_dialog.show_more_label")}
                        </button>
                        {
                            fetchingMore[entity.name] &&
                            <InnerLoader borderColor="green" />
                        }
                    </li>
                }
            </ul>
        </div>
    )
};

export default AliceRecommendedAttributes;